<script setup lang="ts">
import InstallersTable from "@/components/installer/InstallersTable.vue";
import InlineLoader from "@/components/Loaders/InlineLoader.vue";
import SecondaryPageHeader from "@/components/PageHeader/SecondaryPageHeader.vue";
import InstallersSidemenu from "@/components/Sidebar/installer/InstallersSidemenu.vue";
import { useTranslate } from "@/composables/useTranslate";
import MainLayout from "@/layouts/MainLayout.vue";
import { Worker } from "@/types";
import client from "@/utils/axios";
import { computed, onMounted, ref } from "vue";

// Refs
const workers = ref<Worker[]>([]);
const searchInput = ref<string>("");
const selectedMonths = ref<Record<string, string>>({});
const isLoading = ref<boolean>(false);
const isSearchFocused = ref<boolean>(false);
const { t } = useTranslate();

// Functions
const getAllInstallers = async () => {
  isLoading.value = true;
  try {
    const response = await client.get("/installers");
    workers.value = response.data;
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};

const handleSelectMonth = (workerId: string, month: string) => {
  selectedMonths.value[workerId] = month;
};

// Computed properties
const filteredWorkers = computed(() => {
  return workers.value.filter((item: Worker) => {
    let searchQuery = searchInput.value
      ? searchInput.value.trim().toLowerCase()
      : "";
    return item.fullname.toLowerCase().includes(searchQuery);
  });
});

// Lifecycle hooks
onMounted(() => {
  getAllInstallers();
});
</script>

<template>
  <MainLayout>
    <SecondaryPageHeader :is-installers="true" />
    <div class="w-full h-[calc(100vh-52px)] overflow-y-auto flex">
      <div
        class="w-[190px] mt-10 flex flex-col items-center gap-3"
        v-if="isLoading"
      >
        <InlineLoader />
        <InlineLoader />
        <InlineLoader />
        <InlineLoader />
      </div>
      <div class="min-w-[190px] w-[190px]" v-else>
        <InstallersSidemenu
          :workers="workers"
          :selected-months="selectedMonths"
          @select-month="handleSelectMonth"
        />
      </div>
      <div class="w-full h-full flex flex-col">
        <div class="max-w-[996px] p-[31px] mx-auto flex flex-col h-full">
          <!-- Search -->
          <div class="flex items-center gap-2 mb-[19px] w-[996px]">
            <h2
              class="text-xl leading-8 font-semibold text-primary-text-color tracking-[0.01rem]"
            >
              {{ t("installers.all_installers") }}
            </h2>
            <form
              class="w-[302px] h-[30px] px-[10px] flex items-center gap-[6px] bg-transparent border border-[#A7A5AE] rounded-primary-radius"
              :class="`${isSearchFocused ? 'bg-white' : ''}`"
            >
              <img
                class="w-4 h-4"
                src="@/assets/search-redesign.svg"
                alt="Search"
              />
              <input
                v-model="searchInput"
                class="w-full outline-none bg-transparent text-sm font-normal leading-5 text-[#898790] placeholder:text-[#898790]"
                type="text"
                :placeholder="
                  isSearchFocused
                    ? t('installers.installer_name').value
                    : t('home.search').value
                "
                @focus="isSearchFocused = true"
                @blur="isSearchFocused = false"
              />
            </form>
          </div>
          <!-- Table -->
          <InstallersTable v-if="filteredWorkers" :workers="filteredWorkers" />
        </div>
      </div>
    </div>
  </MainLayout>
</template>
